/** GA4 (2026-10) — loads ONLY when (a) an admin has configured a Measurement ID in
 * Manager → Settings AND (b) the guest tapped OK on the one-line analytics prompt.
 * Until both are true nothing Google touches the page: no script, no cookie, no request.
 *
 * POPIA discipline (pinned by backend/tests/ga_analytics_test.py — keep in sync):
 *   - Event builders below are PURE and whitelist every parameter. No emails, names,
 *     tokens, order/table/room numbers, phone numbers — ever. Only bounded classes.
 *   - page_view sends pathname only (never query strings) and tokened path segments
 *     (/claim/…, /t/…, /check/…) are masked before they leave the device.
 *   - Advertising features are hard-off (google signals + ad personalization denied).
 *   - GA4 masks IPs by design (no legacy anonymize_ip flag exists or is needed).
 *   - Staff/BOH routes are never tracked — engagement numbers mean guests, not tablets.
 */

const SCRIPT_ID = "indg-ga4";
const CHOICE_KEY = "indg-analytics-consent"; // "granted" | "denied" | absent
const REF_KEY = "indg-analytics-ref"; // anonymous subject_ref for the consent_log row

let activeId = null;

// ---------- consent choice (remembered so the bar never re-nags) ----------
export const getConsentChoice = () => {
  try {
    const v = localStorage.getItem(CHOICE_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
};

export const setConsentChoice = (granted) => {
  try {
    localStorage.setItem(CHOICE_KEY, granted ? "granted" : "denied");
  } catch { /* storage blocked — the bar simply re-asks next visit */ }
};

export const clearConsentChoice = () => {
  try {
    localStorage.removeItem(CHOICE_KEY);
  } catch { /* ignore */ }
};

/** Anonymous, locally-generated ref for the consent_log row — never identifies anyone. */
export const anonRef = () => {
  try {
    let r = localStorage.getItem(REF_KEY);
    if (!r) {
      r = `anon-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(REF_KEY, r);
    }
    return r;
  } catch {
    return "anon-ephemeral";
  }
};

// ---------- customer-surface gate (staff/BOH never tracked) ----------
const BOH_PREFIXES = ["/staff", "/invoice/", "/docket-batch/", "/docket-slip/",
                      "/kitchen-slip/", "/gl-schema", "/z-reading/", "/vet/", "/satellite/"];

export const isCustomerRoute = (pathname) =>
  !BOH_PREFIXES.some((p) => pathname === p.replace(/\/$/, "") || pathname.startsWith(p));

/** Tokened path segments are masked so claim/check tokens never reach Google. */
export const sanitizePath = (pathname) => {
  const masked = ["/claim/", "/t/", "/check/", "/corp/pay/", "/corp/set-password/"];
  for (const p of masked) {
    if (pathname.startsWith(p)) return `${p}masked`;
  }
  return pathname;
};

// ---------- loader (playbook: dynamic injection, SPA page views manual) ----------
const validId = (id) => typeof id === "string" && /^G-[A-Z0-9]{4,20}$/.test(id);

export function loadGA(measurementId) {
  if (!validId(measurementId) || getConsentChoice() !== "granted") return false;
  if (activeId === measurementId && document.getElementById(SCRIPT_ID)) return true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  window.gtag("config", measurementId, {
    send_page_view: false, // SPA: one manual page_view per route change, never doubled
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  const s = document.createElement("script");
  s.id = SCRIPT_ID;
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  s.onerror = () => { activeId = null; s.remove(); };
  document.head.appendChild(s);
  activeId = measurementId;
  return true;
}

/** Withdrawal: stop future collection and drop GA cookies (past data follows the
 * deletion/retention process in the Privacy Notice — a tag can't retract history). */
export function removeGA() {
  document.getElementById(SCRIPT_ID)?.remove();
  activeId = null;
  delete window.gtag;
  delete window.dataLayer;
  document.cookie.split(";").forEach((part) => {
    const name = part.split("=")[0].trim();
    if (name === "_ga" || name.startsWith("_ga_")) {
      document.cookie = `${name}=; Max-Age=0; path=/`;
    }
  });
}

// ---------- events (pure builders — the ONLY payload shapes that exist) ----------
export const buildPageViewEvent = (pathname) => ({
  name: "page_view",
  params: {
    page_location: window.location.origin + sanitizePath(pathname),
    page_path: sanitizePath(pathname),
  },
});

export const buildOrderPlacedEvent = (order) => ({
  name: "order_placed",
  params: {
    value: Math.round(order?.total_cents || 0) / 100, // rands
    currency: "ZAR",
    channel: String(order?.channel || ""), // DINE | HOTEL | OFFICE — a class, not an identifier
  },
});

export const buildMenuViewedEvent = (context) => ({
  name: "menu_viewed",
  params: { context: String(context || "browse") }, // dine | hotel | office | browse
});

export const buildLoyaltySignupEvent = () => ({ name: "loyalty_signup", params: {} });

export const buildGameSessionEvent = (gameId) => ({
  name: "game_session_started",
  params: { game: String(gameId || "") },
});

/** No-op unless GA is live (configured + consented + script injected). */
export const track = (event) => {
  if (typeof window.gtag !== "function" || getConsentChoice() !== "granted") return;
  window.gtag("event", event.name, event.params);
};

export const pageView = (pathname) => track(buildPageViewEvent(pathname));
export const trackOrderPlaced = (order) => track(buildOrderPlacedEvent(order));
export const trackMenuViewed = (context) => track(buildMenuViewedEvent(context));
export const trackLoyaltySignup = (verifyResult) => {
  if (verifyResult?.created) track(buildLoyaltySignupEvent());
};
export const trackGameSession = (gameId) => track(buildGameSessionEvent(gameId));
